import type { ActionCodeSettings, User } from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { appUrl } from './domains';

export const PASSWORD_RESET_SENT_MESSAGE =
  'If an account exists for that email, we sent a link to reset your password. Check your inbox and spam folder.';

export const MIN_PASSWORD_LENGTH = 8;

export function hasPasswordProvider(user: User | null): boolean {
  if (!user) return false;
  return user.providerData.some((p) => p.providerId === 'password');
}

export function hasGoogleProvider(user: User | null): boolean {
  if (!user) return false;
  return user.providerData.some((p) => p.providerId === 'google.com');
}

export function getPasswordResetActionCodeSettings(): ActionCodeSettings {
  return {
    url: appUrl('/login'),
    handleCodeInApp: false,
  };
}

export function normalizeAuthEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function validateResetEmail(email: string): string | null {
  const normalized = normalizeAuthEmail(email);
  if (!normalized) return 'Enter your email address.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) {
    return 'Enter a valid email address.';
  }
  return null;
}

export function validateNewPassword(password: string, confirm: string): string | null {
  if (!password) return 'Enter a password.';
  if (password.length < MIN_PASSWORD_LENGTH) {
    return `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
  }
  if (password !== confirm) return 'Passwords do not match.';
  return null;
}

export function getAuthPasswordErrorMessage(error: unknown): string {
  if (!(error instanceof FirebaseError)) {
    return error instanceof Error ? error.message : 'Something went wrong. Please try again.';
  }

  switch (error.code) {
    case 'auth/weak-password':
      return 'Password is too weak. Use at least 8 characters with a mix of letters and numbers.';
    case 'auth/requires-recent-login':
      return 'For security, sign out and sign in again with Google, then retry.';
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Current password is incorrect.';
    case 'auth/email-already-in-use':
    case 'auth/credential-already-in-use':
      return 'This email already has a password. Use “Change password” or reset it from the login page.';
    case 'auth/provider-already-linked':
      return 'A password is already set for this account.';
    case 'auth/popup-closed-by-user':
      return 'Google sign-in was cancelled. Try again to confirm your identity.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Wait a few minutes and try again.';
    case 'auth/invalid-email':
      return 'Enter a valid email address.';
    case 'auth/missing-email':
      return 'Enter your email address.';
    case 'auth/user-not-found':
      return PASSWORD_RESET_SENT_MESSAGE;
    default:
      return error.message || 'Something went wrong. Please try again.';
  }
}
